"use client";

import { useCallback, useEffect, useRef } from "react";

type Options = {
  /** False once the server (or the retention cap) says there is nothing more. */
  hasMore: boolean;
  /** True while a page is in flight, so the sentinel cannot ask twice. */
  isFetching: boolean;
  /** Fetch the next page. */
  onLoadMore: () => void;
  /**
   * How far below the viewport the sentinel counts as visible. The default
   * starts the fetch while the last row is still on screen, so the new items
   * are usually there before the user reaches the end.
   */
  rootMargin?: string;
};

/**
 * Loads the next page when the end of the list comes into view.
 *
 * =========================================================================
 * THIS RESTORES SOMETHING THAT WAS REMOVED ON PURPOSE — READ WHY
 * =========================================================================
 *
 * An intersection observer used to be the ONLY way to page these grids, and it
 * was replaced by a Load More button with a recorded reason: the list was
 * unreachable by keyboard (WCAG 2.1.1), and nothing bounded how much DOM it
 * accumulated.
 *
 * Both concerns are answered here rather than reverted:
 *
 *  - THE BUTTON STAYS IN THE DOM. The caller renders it `sr-only
 *    focus:not-sr-only`, so it is invisible to a pointer user and a real,
 *    focusable, labelled control for anyone arriving by keyboard or AT. Nobody
 *    loses the affordance; it stops taking up the page for people who were
 *    going to scroll anyway.
 *  - THE BOUND WAS NEVER THE BUTTON. `useInfiniteGrid` caps retained pages at
 *    `DEFAULT_MAX_RETAINED_PAGES` and offers a `?page=` re-entry past it — that
 *    hook's own comment argues, correctly, that "the button is the bound" was a
 *    statement about intent and not a ceiling. The ceiling is untouched, so
 *    auto-loading cannot grow the DOM any further than pressing the button
 *    could.
 *
 * THE CAP IS ALSO WHAT KEEPS THE FOOTER REACHABLE. A list that reloads forever
 * is a list whose footer no one can ever reach; this one stops after the cap
 * and hands over to a link.
 *
 * NO OBSERVER, NO PROBLEM. If `IntersectionObserver` is missing the hook simply
 * does nothing, and the button — already rendered, only visually hidden — is
 * still there to be found.
 */
export function useInfiniteScrollSentinel({
  hasMore,
  isFetching,
  onLoadMore,
  rootMargin = "400px",
}: Options) {
  const nodeRef = useRef<HTMLElement | null>(null);
  // Read through a ref so a changing callback identity does not tear down and
  // rebuild the observer on every render of a list that is actively scrolling.
  const loadMoreRef = useRef(onLoadMore);
  useEffect(() => {
    loadMoreRef.current = onLoadMore;
  });

  const setNode = useCallback((node: HTMLElement | null) => {
    nodeRef.current = node;
  }, []);

  useEffect(() => {
    const node = nodeRef.current;
    // `isFetching` is in the dependency list so the observer is rebuilt once the
    // page lands — an element already intersecting fires no new event, and
    // without this a fast scroller would stop paging at the bottom of the list.
    if (!node || !hasMore || isFetching) return;
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMoreRef.current();
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isFetching, rootMargin]);

  return setNode;
}
